import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import type { ChangeEvent } from "react";
import {
  SearchField as _SearchField,
  Button,
  type ButtonProps,
  Input,
  type InputProps,
  type SearchFieldProps,
} from "react-aria-components";

import { cn } from "@/lib/utils";

function SearchFieldInput({ className, ...props }: InputProps) {
  return (
    <Input
      className={cn(
        "min-w-0  flex-1 bg-background px-2 py-1.5 outline-solid outline-0",
        "[&::-webkit-search-cancel-button]:hidden",
        className,
      )}
      {...props}
    />
  );
}

function SearchField({ className, ...props }: SearchFieldProps) {
  return (
    <_SearchField
      className={cn(
        "bg-white border-0 border-gray-200 flex focus-visible:ring-2 focus-visible:ring-blue-750 focus-visible:ring-offset-2 focus-within:border-slate-700/40 focus-within:outline-hidden focus-within:ring-0 focus-within:ring-slate-400 group group-data-disabled:opacity-50 items-center placeholder:text-muted-foreground px-0 ring-offset-background rounded-none text-xxs",
        className,
      )}
      {...props}
    />
  );
}

function SearchFieldClear({ className, ...props }: ButtonProps) {
  return (
    <Button
      className={cn(
        "mr-1 rounded-xs opacity-70 ring-offset-background transition-opacity",
        "hover:opacity-100 group-data-disabled:pointer-events-none",
        "group-data-empty:invisible",
        className,
      )}
      {...props}
    />
  );
}

export interface InlineSearchFieldProps {
  ariaLabel: string;
  clearAriaLabel: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
}

function InlineSearchField({
  ariaLabel,
  clearAriaLabel,
  value,
  onValueChange,
  placeholder = "Search...",
}: InlineSearchFieldProps) {
  return (
    <div className="flex h-[30px] w-full max-w-[356px] items-stretch">
      <SearchField
        aria-label={ariaLabel}
        value={value}
        onClear={() => onValueChange("")}
        className="min-w-0 flex-1 items-center rounded-none rounded-l-md border border-gray-200 bg-white py-0 pl-3.5 pr-1 shadow-[0px_1px_1px_rgba(18,18,23,0.05)] focus-within:border-gray-200 focus-within:ring-0"
      >
        <SearchFieldInput
          className="h-full min-h-0 px-0 py-0 text-xs tracking-[0.12px] text-gray-900 placeholder:text-gray-600"
          placeholder={placeholder}
          value={value}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onValueChange(event.target.value)
          }
        />
        <SearchFieldClear aria-label={clearAriaLabel}>
          <XIcon aria-hidden className="size-3.5" />
        </SearchFieldClear>
      </SearchField>
      <div
        className="flex h-full w-[33px] shrink-0 items-center justify-center rounded-r-md border border-l-0 border-gray-200 bg-white"
        aria-hidden
      >
        <MagnifyingGlassIcon
          aria-hidden
          size={16}
          className="shrink-0 text-gray-600"
        />
      </div>
    </div>
  );
}

export { InlineSearchField, SearchField, SearchFieldClear, SearchFieldInput };
